import { UnifiedThemeOptions } from '@backstage/theme';
import {
  ModeTokens,
  brand,
  deepTealAlpha,
  fonts,
  navigation,
  radius,
  shadow,
  tealAlpha,
} from './tokens';

/**
 * Builds the full set of MUI + Backstage component overrides for a mode.
 *
 * Focus areas: sidebar chrome, headers, catalog tables (soft dividers +
 * zebra striping), chips/badges, buttons, inputs and cards.
 */
export const buildComponents = (
  mode: 'light' | 'dark',
  t: ModeTokens,
): UnifiedThemeOptions['components'] => {
  const nav = navigation[mode];
  const isDark = mode === 'dark';
  const cardShadow = isDark ? shadow.cardDark : shadow.card;
  const raisedShadow = isDark ? shadow.raisedDark : shadow.raised;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: fonts.sans,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          margin: 0,
          padding: 0,
          width: '100%',
          height: '100%',
          overflowX: 'hidden',
          backgroundColor: t.canvas,
        },
        '#root': {
          width: '100%',
          height: '100%',
          overflowX: 'hidden',
        },
      },
    },

    /* ── Layout containers (preserve existing overflow guards) ─────────── */
    BackstageContent: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      },
    },
    MuiGrid: {
      // MUST be re-declared. createUnifiedTheme merges component themes with a
      // shallow spread ({ ...defaultComponentThemes, ...options.components }),
      // so supplying MuiGrid here REPLACES Backstage's entry — including its
      // `defaultProps: { spacing: 2 }`. Dropping it silently removes the gutter
      // from every Grid that doesn't pass `spacing` explicitly, which is what
      // flattened the gap between the filter rail and the results table on the
      // Service Catalog, APIs and Docs pages (all three render a
      // spacing-less CatalogFilterLayout grid).
      defaultProps: {
        spacing: 2,
      },
      styleOverrides: {
        // No `maxWidth: 100%` here. A spaced grid is sized
        // `width: calc(100% + 16px)` with `margin: -8px`; clamping it to 100%
        // keeps the gutter but shortens the row, costing 16px of usable width
        // and shifting it 8px left. Viewport overflow is already guarded higher
        // up on BackstagePage / BackstageContent / MuiContainer / body / #root,
        // so the clamp bought nothing here.
        container: {
          overflowX: 'hidden',
        },
      },
    },

    /* ── Sidebar chrome ─────────────────────────────────────────────────── */
    // Flat surface in both modes: a WHITE panel in light mode, a near-black
    // panel in dark mode, separated from the page by a hairline border.
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: nav.background,
          borderRight: `1px solid ${nav.border}`,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          color: nav.color,
          textDecoration: 'none',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            backgroundColor: nav.hoverBackground,
            color: t.text,
          },
          '&:focus-visible': {
            outline: `2px solid ${nav.indicator}`,
            outlineOffset: -2,
          },
        },
        label: {
          fontFamily: fonts.sans,
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: 0,
        },
        selected: {
          '&$root': {
            background: t.primary50,
            borderLeft: `solid 3px ${nav.indicator}`,
            color: nav.selectedColor,
          },
          '&$root:hover': {
            background: t.primary50,
          },
          '& $label': {
            fontWeight: 600,
          },
          '& $iconContainer': {
            color: nav.indicator,
          },
        },
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: {
          background: nav.border,
          margin: '10px 18px',
        },
      },
    },

    /* ── Page headers ───────────────────────────────────────────────────── */
    BackstageHeader: {
      styleOverrides: {
        header: {
          padding: '20px 24px',
          boxShadow: 'none',
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
        },
        title: {
          fontFamily: fonts.sans,
          fontSize: '1.5rem',
          fontWeight: 700,
          letterSpacing: '-0.015em',
        },
        subtitle: {
          color: 'rgba(255, 255, 255, 0.78)',
          fontWeight: 400,
        },
        type: {
          color: 'rgba(255, 255, 255, 0.65)',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
        },
      },
    },
    BackstageContentHeader: {
      styleOverrides: {
        title: {
          fontFamily: fonts.sans,
          fontSize: '1.25rem',
          fontWeight: 700,
          letterSpacing: '-0.01em',
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontFamily: fonts.sans,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          color: 'rgba(255, 255, 255, 0.65)',
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        },
        value: {
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
        },
      },
    },

    /* ── Tables (catalog + everywhere else) ─────────────────────────────── */
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          borderTop: 'none',
          borderBottom: `1px solid ${t.border}`,
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          padding: '14px 20px 10px 20px',
        },
        searchField: {
          backgroundColor: t.surfaceSubtle,
          border: `1px solid ${t.border}`,
          borderRadius: radius.md,
          padding: '2px 12px',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:focus-within': {
            borderColor: t.primary,
            boxShadow: `0 0 0 3px ${t.focusRing}`,
          },
          '& .MuiInput-underline:before': { borderBottom: 'none' },
          '& .MuiInput-underline:after': { borderBottom: 'none' },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          fontSize: '0.8125rem',
          lineHeight: 1.45,
          borderBottom: `1px solid ${t.divider}`,
          fontVariantNumeric: 'tabular-nums',
        },
        head: {
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: t.textMuted,
          padding: '10px 16px',
          borderBottom: `1px solid ${t.border}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.12s ease',
          '&:nth-of-type(even)': {
            backgroundColor: t.zebra,
          },
          '&:not(.MuiTableRow-head):hover': {
            backgroundColor: t.rowHover,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': { color: t.text },
          '&.Mui-active': { color: t.text },
        },
      },
    },

    /* ── Chips / badges ─────────────────────────────────────────────────── */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          height: 22,
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.01em',
          backgroundColor: t.surfaceSubtle,
          color: t.textMuted,
          margin: '0 6px 6px 0',
        },
        label: {
          padding: '0 8px',
        },
        outlined: {
          borderColor: t.border,
          backgroundColor: 'transparent',
        },
        clickable: {
          '&:hover': {
            backgroundColor: isDark ? tealAlpha(0.14) : deepTealAlpha(0.1),
            color: t.primary,
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${t.focusRing}`,
          },
        },
        deleteIcon: {
          width: 14,
          height: 14,
          marginRight: 6,
        },
      },
    },

    /* ── Cards & surfaces ───────────────────────────────────────────────── */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: radius.lg,
        },
        elevation1: {
          boxShadow: cardShadow,
        },
        elevation2: {
          boxShadow: cardShadow,
        },
        elevation8: {
          boxShadow: raisedShadow,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${t.border}`,
          boxShadow: cardShadow,
          backgroundColor: t.surface,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 20px 8px 20px',
        },
        title: {
          fontSize: '1rem',
          fontWeight: 700,
          letterSpacing: '-0.005em',
        },
        subheader: {
          fontSize: '0.8125rem',
          color: t.textMuted,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px 20px 16px 20px',
        },
      },
    },

    /* ── Buttons ────────────────────────────────────────────────────────── */
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          padding: '6px 16px',
          minHeight: 34,
          letterSpacing: 0,
          transition:
            'background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${t.focusRing}`,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: raisedShadow,
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: t.primary,
          color: t.primaryContrast,
          '&:hover': {
            backgroundColor: t.primaryHover,
          },
        },
        outlined: {
          borderColor: t.border,
          '&:hover': {
            borderColor: t.primary,
            backgroundColor: isDark ? tealAlpha(0.08) : deepTealAlpha(0.05),
          },
        },
        sizeSmall: {
          fontSize: '0.75rem',
          padding: '4px 12px',
          minHeight: 28,
        },
      },
    },

    /* ── Inputs / search ────────────────────────────────────────────────── */
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontFamily: fonts.sans,
        },
        input: {
          '&::placeholder': {
            color: t.textMuted,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: t.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.textSubtle,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.primary,
            borderWidth: 1,
          },
        },
        input: {
          padding: '10px 12px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          border: `1px solid ${t.border}`,
          boxShadow: raisedShadow,
        },
        option: {
          fontSize: '0.875rem',
        },
      },
    },

    /* ── Catalog filter sidebar ─────────────────────────────────────────── */
    // The CatalogReact* override names are registered by a module
    // augmentation in @backstage/plugin-catalog-react that resolves to a
    // different virtual instance of @backstage/theme, so the key is not
    // visible to the type checker here. The runtime merge handles it fine.
    ...({
      CatalogReactUserListPicker: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: `1px solid ${t.border}`,
            borderRadius: radius.lg,
          },
          title: {
            textTransform: 'capitalize',
          },
        },
      },
    } as UnifiedThemeOptions['components']),

    /* ── Misc polish ────────────────────────────────────────────────────── */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: radius.sm,
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: isDark ? '#1E3237' : '#122126',
          padding: '6px 10px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.xl,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          border: `1px solid ${t.border}`,
          boxShadow: raisedShadow,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: t.divider,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 5,
        },
        bar: {
          borderRadius: 999,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          letterSpacing: 0,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: { letterSpacing: '-0.02em' },
        h2: { letterSpacing: '-0.02em' },
        h3: { letterSpacing: '-0.01em' },
        h4: { letterSpacing: '-0.01em' },
        body1: { fontSize: '0.875rem', lineHeight: 1.6 },
        body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
        caption: { fontSize: '0.75rem', color: t.textMuted },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: isDark ? brand.teal300 : brand.teal700,
          fontWeight: 500,
        },
      },
    },
  };
};
